import ApiService from '@shared/services/api.service';

const endPoint = '/urls';

const WebpageService = {
  /**
   * Fetch a simple webpage list as a flat array.
   *
   * When called with no page, fetches all pages and returns one array.
   * When called with page, returns only that page's rows as an array.
   *
   * @param {Object} options - Query options
   * @param {number|null} options.page - Page number (1-based)
   * @param {number} options.itemsPerPage - Items per page (default: 1000)
   * @param {string} options.q - Search term
   * @param {number|null} options.userId - Filter by user ID
   * @returns {Promise<Array>}
   */
  async getSimpleURLList(options = {}) {
    const {
      page = null,
      itemsPerPage = 1000,
      q = '',
      userId = null
    } = options;

    const params = new URLSearchParams();
    if (page) {
      params.append('page', page);
    }
    params.append('items_per_page', itemsPerPage);
    if (q) {
      params.append('q', q);
    }
    if (userId) {
      params.append('user_id', userId);
    }

    const url = `${endPoint}/simple?${params.toString()}`;
    const response = await ApiService.get(url);

    if (Array.isArray(response)) {
      return response;
    }
    if (page) {
      return response && Array.isArray(response.data) ? response.data : [];
    }
    if (response && response.data && response.meta) {
      let allData = [...response.data];
      const { current_page, last_page } = response.meta;
      if (current_page < last_page) {
        const pagePromises = [];
        for (let p = current_page + 1; p <= last_page; p++) {
          pagePromises.push(ApiService.get(`${url}&page=${p}`));
        }
        const pageResponses = await Promise.all(pagePromises);
        for (const pageResponse of pageResponses) {
          if (pageResponse && Array.isArray(pageResponse.data)) {
            allData = allData.concat(pageResponse.data);
          }
        }
      }
      return allData;
    }
    return response || [];
  },

  /**
   * Fetch webpages with optional server-side pagination and filtering.
   *
   * When called with no arguments or only userId, fetches ALL pages and returns a flat array.
   * When called with pagination options, returns the paginated response with data, links, and meta.
   *
   * @param {Object} options - Query options
   * @param {number|null} options.page - Page number (1-based). If provided, returns paginated response.
   * @param {number} options.itemsPerPage - Number of items per page (default: 100)
   * @param {string} options.q - Search term (searches config_name and url_name)
   * @param {number|null} options.userId - Filter by user ID
   * @param {Array<number>} options.tagIds - Filter by tag IDs
   * @param {Array<string>} options.statuses - Filter by health statuses (e.g., ['OK', 'CRITICAL'])
   * @param {Array<string>} options.planTypes - Filter by plan types (e.g., ['GOLD', 'TRIAL'])
   * @param {number|null} options.statusCode - Filter by HTTP status code
   * @param {boolean} options.archived - When true, list only archived webpages (they are excluded otherwise)
   * @returns {Promise<Array|{data: Array, links: Object, meta: Object}>} - Flat array (all pages) or paginated response
   */
  async getList(options = {}) {
    const { 
      page = null, 
      itemsPerPage = 100, 
      q = '', 
      userId = null, 
      tagIds = [], 
      statuses = [], 
      planTypes = [],
      statusCode = null,
      archived = false,
      exact = false
    } = options;
    
    // Build query string
    const params = new URLSearchParams();
    
    if (page) {
      params.append('page', page);
    }
    params.append('items_per_page', itemsPerPage);

    if (archived) {
      params.append('archived', 'true');
    }
    
    if (q) {
      params.append('q', q);
    }

    if (exact) {
      params.append('exact', 'true');
    }
    
    if (userId) {
      params.append('user_id', userId);
    }
    
    if (Array.isArray(tagIds) && tagIds.length > 0) {
      tagIds.forEach(id => params.append('tag_ids[]', id));
    }
    
    if (Array.isArray(statuses) && statuses.length > 0) {
      statuses.forEach(s => params.append('statuses[]', s));
    }
    
    if (Array.isArray(planTypes) && planTypes.length > 0) {
      planTypes.forEach(pt => params.append('plan_types[]', pt));
    }
    
    if (statusCode) {
      params.append('status_code', statusCode);
    }
    
    const url = `${endPoint}?${params.toString()}`;
    const response = await ApiService.get(url);
    
    // Handle non-paginated response (backwards compatibility)
    if (Array.isArray(response)) {
      return response;
    }
    
    // If page was specified, return paginated response as-is
    if (page) {
      return response;
    }
    
    // No page specified: fetch all pages by getting 1 first, then fetch remaining pages in batches
    if (response && response.data && response.meta) {
      let allData = [...response.data];
      const { current_page, last_page } = response.meta;
      
      // Fetch remaining pages if there are more
      if (current_page < last_page) {
        const pagePromises = [];
        for (let p = current_page + 1; p <= last_page; p++) {
          pagePromises.push(ApiService.get(`${url}&page=${p}`));
        }
        
        const pageResponses = await Promise.all(pagePromises);
        for (const pageResponse of pageResponses) {
          if (pageResponse && Array.isArray(pageResponse.data)) {
            allData = allData.concat(pageResponse.data);
          }
        }
      }
      
      return allData;
    }
    
    // Fallback: return response as-is if structure is unexpected
    return response || [];
  },

  create(bodyParams) {
    return ApiService.post(`${endPoint}`, bodyParams);
  },

  createForDomain(domain, bodyParams) {
    return ApiService.post(`domains/${domain}/urls`, bodyParams);
  },

  createMultiples(bodyParams) {
    return ApiService.post('batch_jobs/baseline', bodyParams);
  },

  createMultiplesForDomain(bodyParams) {
    return ApiService.post('batch_jobs/baseline_domain', bodyParams);
  },

  getRecommendations(url) {
    return ApiService.get(`${endPoint}/${url}/baseline_recommendation`);
  },

  updateBaselineSetting(url, bodyParams) {
    return ApiService.post(`${endPoint}/${url}/store_baseline`, bodyParams);
  },
  updateUrlConfig(url, bodyParams) {
    return ApiService.put(`${endPoint}/${url}`, bodyParams);
  },
  putAiTriagePreferences(urlId, bodyParams) {
    return ApiService.put(`${endPoint}/${urlId}/ai-triage/preferences`, bodyParams);
  },
  putAiTriagePrompt(urlId, bodyParams) {
    return ApiService.put(`${endPoint}/${urlId}/ai-triage/prompt`, bodyParams);
  },
  getAiTriageForFqdn(fqdnId) {
    return ApiService.get(`ai-triage/fqdns/${fqdnId}`);
  },
  putAiTriagePreferencesByFqdn(fqdnId, bodyParams) {
    return ApiService.put(`ai-triage/fqdns/${fqdnId}/ai-triage/preferences`, bodyParams);
  },
  putAiTriagePromptByFqdn(fqdnId, bodyParams) {
    return ApiService.put(`ai-triage/fqdns/${fqdnId}/ai-triage/prompt`, bodyParams);
  },
  putAiTriageWhitelistedDomainsByFqdn(fqdnId, bodyParams) {
    return ApiService.put(`ai-triage/fqdns/${fqdnId}/ai-triage/whitelisted-domains`, bodyParams);
  },
  fetchAiTriageFqdns() {
    return ApiService.get('fqdns');
  },
  updateUrlConfigBulk(urls,bodyParams) {
    const body = { urls, ...bodyParams };
    return ApiService.put(`${endPoint}/bulk_update`, body);
  },
  get(url) {
    return ApiService.get(`${endPoint}/${url}`);
  },

  delete(url) {
    return ApiService.delete(`${endPoint}/${url}`);
  },
  getImagesAnalytics(url) {
    return ApiService.get(`${endPoint}/${url}/baseline_screenshot`);
  },
  getPerformance(url, query) {
    return ApiService.get(`${endPoint}/${url}/response_performance${query}`);
  },
  getNotificationConfigurations(url) {
    return ApiService.get(`${endPoint}/${url}/notification_configurations`);
  },
  reBaselineWebpage(urlId) {
    return ApiService.post(`${endPoint}/${urlId}/rebaseline`);
  },
  /**
   * Archive one or more webpages. Monitoring stops and the plan slot is freed;
   * alerts, policies and history are kept for audit.
   * @param {number[]} urlIds - URL IDs to archive (a single archive is a one-element array)
   * @param {string|null} remark - Optional remark stored on each archive record
   */
  archive(urlIds, remark = null) {
    const body = { url_ids: urlIds };
    if (remark) {
      body.remark = remark;
    }
    return ApiService.post(`${endPoint}/batch_archive`, body);
  },
  /**
   * Restore one or more archived webpages. Monitoring resumes with the last baseline
   * (no rebaseline). Without plan_type the API restores each onto its original plan; pass
   * plan_type (and optionally polling_interval) to restore the whole batch onto a different plan.
   * @param {number[]} urlIds - URL IDs to restore (a single restore is a one-element array)
   * @param {{plan_type?: string, polling_interval?: number}} bodyParams
   */
  batchRestore(urlIds, bodyParams = {}) {
    const body = { url_ids: urlIds, ...bodyParams };
    return ApiService.post(`${endPoint}/batch_restore`, body);
  },
  reBaselineMultipleWebpages(url_ids) {
    const body = { url_ids };
    return ApiService.post('/batch_jobs/rebaseline', body);
  },
  manualVerifyWebpage(urlId) {
    return ApiService.post(`${endPoint}/${urlId}/manual_verify`);
  },
  nlp_manual_verify(urlId, bodyParams) {
    return ApiService.post(`${endPoint}/${urlId}/nlp/verify`, bodyParams);
  },
  nlp_analysis_request(urlId, bodyParams) {
    return ApiService.post(`${endPoint}/${urlId}/nlp/analysis/add`, bodyParams);
  },
  nlp_analysis_history_list(urlId) {
    return ApiService.get(`${endPoint}/${urlId}/nlp/analysis/list`);
  },
  nlp_analysis_result(urlId, bodyParams) {
    return ApiService.post(`${endPoint}/${urlId}/nlp/analysis/result`, bodyParams);
  },
  nlp_analysis_record_remove(urlId, bodyParams) {
    return ApiService.post(`${endPoint}/${urlId}/nlp/analysis/remove`, bodyParams);
  },
  san_encode_URL(url_string) {
    let url = url_string.trim();
    url = decodeURI(url);
    let uparts = new URL(url);
    //console.log(uparts);

    let scheme = uparts.protocol ? uparts.protocol.substr(0, uparts.protocol.length - 1) : ""; // JS protocol includes ending : whereas PHP scheme does not
    let pass = uparts.password ? uparts.password : "";
    let user = uparts.username ? uparts.username : "";
    let port = uparts.port ? uparts.port : "";
    let host = uparts.hostname ? uparts.hostname : "";
    let path = uparts.pathname ? uparts.pathname : "";
    let query = uparts.search ? uparts.search.substr(1) : ""; // JS search includes starting ? whereas PHP query does not
    let fragment = uparts.hash ? uparts.hash.substr(1) : ""; // JS hash includes starting # whereas PHP fragment does not

    if (scheme) {
      scheme += '://';
    } else {
      scheme = 'http://';
    }

    if (pass && user) {
      user = encodeURIComponent(user) + ':';
      pass = encodeURIComponent(pass) + '@';
    } else if (user) {
      user += '@';
    }

    if (port && host) {
      host = '' + host + ':';
    }

    if (path) {
      let arr = path.split(/([/;=])/);
      path = "";
      for (const v of arr) {
        switch (v) {
          case "/":
          case ";":
          case "=":
            path += v;
            break;
          default:
            path += this.rawurlencode(v); // the new URL() already auto-encodes certain characters
        }
      }
      // legacy patch for servers that need a literal /~username
      path = path.replace("/%7E", "/~");
    }

    if (query) {
      let arr = query.split(/([&=])/);
      query = "?";
      for (const v of arr) {
        if ("&" == v || "=" == v) {
          query += v;
        } else {
          query += this.rawurlencode(v);
        }
      }
    }

    // The fragment handling part we keep it as it is for the frontend
    /*if (fragment) {
        fragment = "";
    }*/

    //console.log("encoded url", [scheme, user, pass, host, port, path, query, fragment].join(''))
    return [scheme, user, pass, host, port, path, query, fragment].join('');
  },
  rawurlencode(str) {
    str = decodeURIComponent(str);

    let chars = [...str];

    // According to the PHP docs: https://www.php.net/manual/en/function.rawurlencode.php
    // Returns a string in which all non-alphanumeric characters except -_.~ have been replaced with a percent (%) sign followed by two hex digits.
    const regex = /^[a-z0-9-_.~]$/i;
    chars = chars.map((v) => {
      if (regex.test(v)) {
        return v;
      } else {
        if (v.charCodeAt(0) < 256) {
          //console.log(v, v.charCodeAt(0));
          return '%' + v.charCodeAt(0).toString(16).padStart(2, "0");
        } else {
          return encodeURIComponent(v);
        }
      }
    });
    return chars.join('');
  },

  /**
   * Fetch baseline HTML for one or more URLs.
   * @param {number|number[]} urlIds - Single URL ID or array of URL IDs to fetch
   * @returns {Promise<{results: Object}>} - Map of urlId to {html, url} or {error, error_code}
   */
  getBaselineHtml(urlIds) {
    const ids = Array.isArray(urlIds) ? urlIds : [urlIds];
    const queryParams = ids.map(id => `url_ids[]=${id}`).join('&');
    return ApiService.get(`${endPoint}/baseline_html?${queryParams}`);
  },

  /**
   * Fetch extracted video thumbnails for a URL.
   * @param {number} urlId - The URL ID
   * @returns {Promise<{thumbnails: Array<{md5: string, data: string}>}>}
   */
  getVideoThumbnails(urlId) {
    return ApiService.get(`${endPoint}/${urlId}/video_thumbnails`);
  },

};
export default WebpageService;
